import { Tier } from 'app/shared/account/tier.model';
import {
  CustomAuthenticationType,
  IntegrationConfig,
  IntegrationDetailsRow,
  IntegrationDropdownOptionValues,
  IntegrationInputConfig,
  IntegrationInputSubType,
  IntegrationInputType,
  IntegrationLineKeyPart,
  IntegrationOptionsParsingType,
  IntegrationPropertyConversion,
  IntegrationType,
  RestApiInformation,
} from './integrations-models';

export class AutomationHelpersIntegrations {
  public static SUPPORTED_APP_TYPES = [
    IntegrationType.RuleMailer,
    IntegrationType.Voyado,
    IntegrationType.MailChimp,
    IntegrationType.Klaviyo,
    IntegrationType.Emarsys,
    IntegrationType.Custobar,
    IntegrationType.Apsis,
    IntegrationType.ApsisOne,
    IntegrationType.SiftLab,
    IntegrationType.Pipedrive,
    IntegrationType.Zapier,
    IntegrationType.MailerLite,
    IntegrationType.MailerSend,
    IntegrationType.Centra,
    IntegrationType.Ftp,
    IntegrationType.Shopify,
    IntegrationType.Brevo,
    IntegrationType.Dotdigital,
    IntegrationType.Omnisend,
    IntegrationType.CleverReach,
    IntegrationType.Shopware,
    IntegrationType.Custom,
  ];

  public static getAllSupportedAutomationAppConfigs(): Array<IntegrationConfig> {
    return this.getAllIntegrationConfigs().filter((app) => this.SUPPORTED_APP_TYPES.indexOf(app.type) !== -1);
  }

  public static getAllIntegrationConfigs(): Array<IntegrationConfig> {
    return [
      this.VOYADO_CONFIG,
      this.MAILCHIMP_CONFIG,
      this.RULE_CONFIG,
      this.KLAVIYO_CONFIG,
      this.EMARSYS_CONFIG,
      this.CUSTOBAR_CONFIG,
      this.APSIS_CONFIG,
      this.APSIS_ONE_CONFIG,
      this.SIFT_LAB_CONFIG,
      this.PIPEDRIVE_CONFIG,
      this.MAILER_LITE_CONFIG,
      this.MAILER_SEND_CONFIG,
      this.ZAPIER_CONFIG,
      this.CENTRA_CONFIG,
      this.FTP_CONFIG,
      this.SHOPIFY_CONFIG,
      this.BREVO_CONFIG,
      this.DOTDIGITAL_CONFIG,
      this.OMNISEND_CONFIG,
      this.CLEVERREACH_CONFIG,
      this.SHOPWARE_CONFIG,
      this.CUSTOM_CONFIG,
    ];
  }

  private static VOYADO_CONFIG = {
    type: IntegrationType.Voyado,
    logoUrl: '/assets/img/logos/voyado.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11051960-integrate-with-voyado',
    descriptionRows: [
      { textKey: 'settings.apps.Voyado.description1' } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Voyado.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Voyado.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Voyado.description4',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Voyado.description5',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Voyado.description6',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiKey',
        required: true,
        labelKey: 'settings.apps.fields.apiKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiKey',
          descriptionKey: 'settings.apps.Voyado.apiKeyExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'url',
        required: true,
        labelKey: 'settings.apps.fields.url',
        explainerContent: {
          headerKey: 'settings.apps.fields.url',
          descriptionKey: 'settings.apps.Voyado.urlExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'encryptionKey',
        required: true,
        labelKey: 'settings.apps.fields.encryptionKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.encryptionKey',
          descriptionKey: 'settings.apps.Voyado.encryptionKeyExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'storeExternalId',
        required: false,
        labelKey: 'settings.apps.Voyado.externalStoreId',
        explainerContent: {
          headerKey: 'settings.apps.Voyado.externalStoreId',
          descriptionKey: 'settings.apps.Voyado.externalStoreIdExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.TextArea,
          key: 'voyadoTriggers',
          required: false,
          labelKey: 'settings.apps.Voyado.automations-label',
          descriptionKey: 'settings.apps.Voyado.automations-descr',
          propertyConversion: IntegrationPropertyConversion.NewLineToCsv,
          placeholderKey: 'settings.apps.Voyado.automations-placeholder',
        },
      ],
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          instruction: 'settings.apps.Voyado.properties-instruction',
          labelKey: 'settings.apps.Voyado.profileProperties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentCheckboxPresent,
              translationKey: 'settings.apps.propertiesToSync.LastConsentCheckboxPresent',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextDomain,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextDomain',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextTitle,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextTitle',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentDate,
              translationKey: 'settings.apps.propertiesToSync.LastConsentDate',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyRevision,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyRevision',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyVersion,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyVersion',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
            // These two are removed according to Olofs recommendation
            /*{key: IntegrationDropdownOptionValues.Name, translationKey: 'settings.apps.propertiesToSync.Name'},
            {key: IntegrationDropdownOptionValues.Optin, translationKey: 'settings.apps.propertiesToSync.Optin'},
            {key: IntegrationDropdownOptionValues.OptinData, translationKey: 'settings.apps.propertiesToSync.OptinData'},
            {key: IntegrationDropdownOptionValues.Org, translationKey: 'settings.apps.propertiesToSync.Org'},
            {key: IntegrationDropdownOptionValues.Rating, translationKey: 'settings.apps.propertiesToSync.Rating'},
            {key: IntegrationDropdownOptionValues.RatingM, translationKey: 'settings.apps.propertiesToSync.RatingM'}*/
          ],
          optionsCheckAll: true,
        },
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'subscriberCheckPreferences',
          required: false,
          instruction: 'settings.apps.Voyado.channels-instruction',
          labelKey: 'settings.apps.Voyado.subscriberCheck-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.AcceptsEmail,
              translationKey: 'settings.apps.Voyado.subscriberChecks.AcceptsEmail',
            },
            {
              key: IntegrationDropdownOptionValues.AcceptsSms,
              translationKey: 'settings.apps.Voyado.subscriberChecks.AcceptsSms',
            },
            {
              key: IntegrationDropdownOptionValues.AcceptsPostal,
              translationKey: 'settings.apps.Voyado.subscriberChecks.AcceptsPostal',
            },
          ],
        },
        // { // Commented out until better solution is done in back-end
        //   type: IntegrationInputType.ToggleButton,
        //   key: 'useVoyadoIdAsIdentity',
        //   required: false,
        //   descriptionKey: 'settings.apps.Voyado.voyadoIdIdentifier-label',
        //   placeholderKey: 'settings.apps.Voyado.voyadoIdIdentifier-descr',
        // },
      ],
      [
        {
          type: IntegrationInputType.SingleChoiceDropdown,
          key: 'defaultContactType',
          required: false,
          labelKey: 'settings.apps.Voyado.primaryContactType-label',
          optionsUrl: '/integrations/Voyado/contact-types',
          explainerContent: {
            headerKey: 'settings.apps.Voyado.primaryContactType-label',
            descriptionKey: 'settings.apps.Voyado.primaryContactExplainer',
          },
        },
        {
          type: IntegrationInputType.SingleChoiceDropdown,
          key: 'memberContactType',
          required: false,
          labelKey: 'settings.apps.Voyado.secondaryContactType-label',
          optionsUrl: '/integrations/Voyado/contact-types',
          explainerContent: {
            headerKey: 'settings.apps.Voyado.secondaryContactType-label',
            descriptionKey: 'settings.apps.Voyado.secondaryContactExplainer',
          },
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
    disabledOnTiers: [Tier.Essential],
  } as IntegrationConfig;

  private static RULE_CONFIG = {
    type: IntegrationType.RuleMailer,
    logoUrl: '/assets/img/icons/rule-icon.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11058575-integrating-with-rule',
    descriptionRows: [
      {
        textKey: 'settings.apps.RuleMailer.description1',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.RuleMailer.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.RuleMailer.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.RuleMailer.description4',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.RuleMailer.description5',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.RuleMailer.description6',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiKey',
        required: true,
        labelKey: 'settings.apps.fields.apiKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiKey',
          descriptionKey: 'settings.apps.RuleMailer.apiKeyExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'listIdsForTrigger',
          required: false,
          labelKey: 'settings.apps.RuleMailer.tags-label',
          optionsUrl: '/integrations/RuleMailer/lists',
        },
      ],
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          instruction: 'settings.apps.RuleMailer.properties-instruction',
          labelKey: 'settings.apps.RuleMailer.profileProperties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentCheckboxPresent,
              translationKey: 'settings.apps.propertiesToSync.LastConsentCheckboxPresent',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextDomain,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextDomain',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextTitle,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextTitle',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentDate,
              translationKey: 'settings.apps.propertiesToSync.LastConsentDate',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyRevision,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyRevision',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyVersion,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyVersion',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
            // These two are removed according to Olofs recommendation
            /*{key: IntegrationDropdownOptionValues.Name, translationKey: 'settings.apps.propertiesToSync.Name'},
            {key: IntegrationDropdownOptionValues.Optin, translationKey: 'settings.apps.propertiesToSync.Optin'},
            {key: IntegrationDropdownOptionValues.OptinData, translationKey: 'settings.apps.propertiesToSync.OptinData'},
            {key: IntegrationDropdownOptionValues.Org, translationKey: 'settings.apps.propertiesToSync.Org'},
            {key: IntegrationDropdownOptionValues.Rating, translationKey: 'settings.apps.propertiesToSync.Rating'},
            {key: IntegrationDropdownOptionValues.RatingM, translationKey: 'settings.apps.propertiesToSync.RatingM'}*/
          ],
          optionsCheckAll: true,
        },
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'listIdsToSync',
          required: false,
          labelKey: 'settings.apps.RuleMailer.tags-update-label',
          optionsUrl: '/integrations/RuleMailer/lists',
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static MAILCHIMP_CONFIG = {
    type: IntegrationType.MailChimp,
    logoUrl: '/assets/img/logos/mailchimp_freddie_color_web.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11059054-integrating-with-mailchimp',
    descriptionRows: [
      {
        textKey: 'settings.apps.MailChimp.description1',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.MailChimp.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.MailChimp.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.MailChimp.description4',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.MailChimp.description5',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiKey',
        required: true,
        labelKey: 'settings.apps.fields.apiKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiKey',
          descriptionKey: 'settings.apps.MailChimp.apiKeyExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'listIdsForTrigger',
          required: false,
          labelKey: 'settings.apps.MailChimp.automationTriggers-label',
          optionsUrl: '/integrations/MailChimp/lists',
        },
      ],
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          instruction: 'settings.apps.MailChimp.properties-instruction',
          labelKey: 'settings.apps.MailChimp.profileProperties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentCheckboxPresent,
              translationKey: 'settings.apps.propertiesToSync.LastConsentCheckboxPresent',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextDomain,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextDomain',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextTitle,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextTitle',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentDate,
              translationKey: 'settings.apps.propertiesToSync.LastConsentDate',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyRevision,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyRevision',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyVersion,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyVersion',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
            // These two are removed according to Olofs recommendation
            /*{key: IntegrationDropdownOptionValues.Name, translationKey: 'settings.apps.propertiesToSync.Name'},
            {key: IntegrationDropdownOptionValues.Optin, translationKey: 'settings.apps.propertiesToSync.Optin'},
            {key: IntegrationDropdownOptionValues.OptinData, translationKey: 'settings.apps.propertiesToSync.OptinData'},
            {key: IntegrationDropdownOptionValues.Org, translationKey: 'settings.apps.propertiesToSync.Org'},
            {key: IntegrationDropdownOptionValues.Rating, translationKey: 'settings.apps.propertiesToSync.Rating'},
            {key: IntegrationDropdownOptionValues.RatingM, translationKey: 'settings.apps.propertiesToSync.RatingM'}*/
          ],
          optionsCheckAll: true,
        },
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'listIdsToSync',
          required: false,
          labelKey: 'settings.apps.MailChimp.updateTriggers-label',
          optionsUrl: '/integrations/MailChimp/lists',
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static MAILER_LITE_CONFIG = {
    type: IntegrationType.MailerLite,
    logoUrl: '/assets/img/logos/mailerlite.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11065404-integrate-with-mailerlite',
    adminOnly: false,
    descriptionRows: [
      { textKey: 'settings.apps.MailerLite.description1' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.MailerLite.description2' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.MailerLite.description3' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.MailerLite.description4' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.MailerLite.description5' } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiToken',
        required: true,
        labelKey: 'settings.apps.fields.apiToken',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiToken',
          descriptionKey: 'settings.apps.MailerLite.apiKeyExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          instruction: 'settings.apps.MailerLite.properties-instruction',
          labelKey: 'settings.apps.MailerLite.profileProperties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentCheckboxPresent,
              translationKey: 'settings.apps.propertiesToSync.LastConsentCheckboxPresent',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextDomain,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextDomain',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextTitle,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextTitle',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentDate,
              translationKey: 'settings.apps.propertiesToSync.LastConsentDate',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyRevision,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyRevision',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyVersion,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyVersion',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
          ],
          optionsCheckAll: true,
        },
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'listIdsToSync',
          required: false,
          labelKey: 'settings.apps.MailerLite.updateTriggers-label',
          optionsUrl: '/integrations/MailerLite/lists',
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static MAILER_SEND_CONFIG = {
    type: IntegrationType.MailerSend,
    logoUrl: '/assets/img/logos/mailer_send.svg',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11406570-integrate-with-mailersend',
    adminOnly: false,
    descriptionRows: [
      { textKey: 'settings.apps.MailerSend.description1' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.MailerSend.description2' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.MailerSend.description3' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.MailerSend.description4' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.MailerSend.description5' } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiToken',
        required: true,
        labelKey: 'settings.apps.fields.apiToken',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiToken',
          descriptionKey: 'settings.apps.MailerSend.apiKeyExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
  } as IntegrationConfig;

  private static KLAVIYO_CONFIG = {
    type: IntegrationType.Klaviyo,
    logoUrl: '/assets/img/logos/klaviyo.svg',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11065284-integrate-with-klaviyo',
    descriptionRows: [
      {
        textKey: 'settings.apps.Klaviyo.description1',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Klaviyo.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Klaviyo.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Klaviyo.description4',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Klaviyo.description5',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Klaviyo.description6',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'privateKey',
        required: true,
        labelKey: 'settings.apps.fields.privateKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.privateKey',
          descriptionKey: 'settings.apps.Klaviyo.privateKeyExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.SegmentPicker,
          key: 'segments',
          required: false,
          labelKey: 'settings.apps.Klaviyo.segments-label',
          descriptionKey: 'settings.apps.Klaviyo.segments-picker-descr',
          propertyConversion: IntegrationPropertyConversion.NewLineToCsv,
        },
        {
          type: IntegrationInputType.TextArea,
          key: 'customProperties',
          required: false,
          labelKey: 'settings.apps.Klaviyo.customProperties-label',
          descriptionKey: 'settings.apps.Klaviyo.customProperties-descr',
          propertyConversion: IntegrationPropertyConversion.NewLineToCsv,
          placeholderKey: 'settings.apps.Klaviyo.customProperties-placeholder',
        },
      ],
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          instruction: 'settings.apps.Klaviyo.properties-instruction',
          labelKey: 'settings.apps.Klaviyo.profileProperties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentCheckboxPresent,
              translationKey: 'settings.apps.propertiesToSync.LastConsentCheckboxPresent',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextDomain,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextDomain',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextTitle,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextTitle',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentDate,
              translationKey: 'settings.apps.propertiesToSync.LastConsentDate',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyRevision,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyRevision',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyVersion,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyVersion',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
          ],
          optionsCheckAll: true,
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static EMARSYS_CONFIG = {
    type: IntegrationType.Emarsys,
    logoUrl: '/assets/img/logos/emarsys.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11093820-integrate-with-emarsys',
    descriptionRows: [
      {
        textKey: 'settings.apps.Emarsys.description1',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Emarsys.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Emarsys.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Emarsys.description4',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Emarsys.description5',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Emarsys.description6',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiKey',
        required: true,
        labelKey: 'settings.apps.fields.username',
        explainerContent: {
          headerKey: 'settings.apps.fields.username',
          descriptionKey: 'settings.apps.Emarsys.usernameExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'secret',
        required: true,
        labelKey: 'settings.apps.fields.secret',
        explainerContent: {
          headerKey: 'settings.apps.fields.secret',
          descriptionKey: 'settings.apps.Emarsys.secretExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.TextArea,
          key: 'customProperties',
          required: false,
          labelKey: 'settings.apps.Emarsys.customProperties-label',
          descriptionKey: 'settings.apps.Emarsys.customProperties-descr',
          propertyConversion: IntegrationPropertyConversion.NewLineToCsv,
          placeholderKey: 'settings.apps.Emarsys.customProperties-placeholder',
        },
      ],
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          instruction: 'settings.apps.Emarsys.properties-instruction',
          labelKey: 'settings.apps.Emarsys.profileProperties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentCheckboxPresent,
              translationKey: 'settings.apps.propertiesToSync.LastConsentCheckboxPresent',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextDomain,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextDomain',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextTitle,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextTitle',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentDate,
              translationKey: 'settings.apps.propertiesToSync.LastConsentDate',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyRevision,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyRevision',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyVersion,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyVersion',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
          ],
          optionsCheckAll: true,
        },
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'listIdsToSync',
          required: false,
          labelKey: 'settings.apps.Emarsys.updateTriggers-label',
          optionsUrl: '/integrations/Emarsys/lists',
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  public static CENTRA_CONFIG = {
    type: IntegrationType.Centra,
    logoUrl: '/assets/img/logos/centra.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11069746-integrating-with-centra',
    adminOnly: false,
    descriptionRows: [
      { textKey: 'settings.apps.Centra.description1' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Centra.description2' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Centra.description3' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Centra.description4' } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'apiUrl',
        required: true,
        labelKey: 'settings.apps.Centra.apiUrl',
        explainerContent: {
          headerKey: 'settings.apps.Centra.apiUrl',
          descriptionKey: 'settings.apps.Centra.apiUrlExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiToken',
        required: true,
        labelKey: 'settings.apps.fields.apiToken',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiToken',
          descriptionKey: 'settings.apps.Centra.apiKeyExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'checkoutApiUrl',
        required: false,
        labelKey: 'settings.apps.Centra.checkoutApiUrl',
        explainerContent: {
          headerKey: 'settings.apps.Centra.checkoutApiUrl',
          descriptionKey: 'settings.apps.Centra.checkoutApiUrlExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'marketIds',
          required: false,
          labelKey: 'settings.apps.Centra.markets',
          optionsUrl: '/integrations/Centra/markets',
          explainerContent: {
            headerKey: 'settings.apps.Centra.markets',
            descriptionKey: 'settings.apps.Centra.marketsExplainer',
          },
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static FTP_CONFIG = {
    type: IntegrationType.Ftp,
    logoUrl: '/assets/img/logos/ftp.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11093709-ftp-service',
    descriptionRows: [
      { textKey: 'settings.apps.Ftp.description1' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Ftp.description2' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Ftp.description3' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Ftp.description4' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Ftp.description5' } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'url',
        required: true,
        labelKey: 'settings.apps.fields.url',
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'username',
        required: true,
        labelKey: 'settings.apps.fields.username',
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'password',
        required: true,
        labelKey: 'settings.apps.Ftp.password',
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.InputField,
          subtype: IntegrationInputSubType.Text,
          key: 'deleteContactsPathToFile',
          required: false,
          // labelKey: 'settings.apps.Ftp.activateDeleteDesc',
          descriptionKey: 'settings.apps.Ftp.deleteContactsPathToFileDesc',
          propertyConversion: IntegrationPropertyConversion.NewLineToCsv,
          placeholderKey: 'settings.apps.Ftp.deleteContactsPathToFilePlaceholder',
        },
        {
          type: IntegrationInputType.ToggleButton,
          key: 'activateDelete',
          labelKey: 'settings.apps.Ftp.activateDeleteDesc',
          required: false,
          // descriptionKey: 'settings.apps.Ftp.activateDeleteDesc',
          placeholderKey: 'settings.apps.Ftp.activateDeletePlaceholder',
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static CUSTOBAR_CONFIG = {
    type: IntegrationType.Custobar,
    logoUrl: '/assets/img/logos/custobar.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11093781-integrate-with-custobar',
    descriptionRows: [
      {
        textKey: 'settings.apps.Custobar.description1',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Custobar.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Custobar.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Custobar.description4',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Custobar.description5',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Custobar.description6',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'accessToken',
        required: true,
        labelKey: 'settings.apps.fields.accessToken',
        explainerContent: {
          headerKey: 'settings.apps.fields.accessToken',
          descriptionKey: 'settings.apps.Custobar.accessTokenExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'url',
        required: true,
        labelKey: 'settings.apps.Custobar.url',
        explainerContent: {
          headerKey: 'settings.apps.Custobar.url',
          descriptionKey: 'settings.apps.Custobar.urlExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.TextArea,
          key: 'events',
          required: false,
          labelKey: 'settings.apps.Custobar.events-label',
          descriptionKey: 'settings.apps.Custobar.events-descr',
          propertyConversion: IntegrationPropertyConversion.NewLineToCsv,
          uniqueLineKey: IntegrationLineKeyPart.WholeLine,
          placeholderKey: 'settings.apps.Custobar.events-placeholder',
        },
        {
          type: IntegrationInputType.TextArea,
          key: 'mailingLists',
          required: false,
          labelKey: 'settings.apps.Custobar.lists-label',
          descriptionKey: 'settings.apps.Custobar.lists-descr',
          propertyConversion: IntegrationPropertyConversion.NewLineToCsv,
          placeholderKey: 'settings.apps.Custobar.lists-placeholder',
        },
      ],
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          instruction: 'settings.apps.Custobar.properties-instruction',
          labelKey: 'settings.apps.Custobar.profileProperties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentCheckboxPresent,
              translationKey: 'settings.apps.propertiesToSync.LastConsentCheckboxPresent',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextDomain,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextDomain',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextTitle,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextTitle',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentDate,
              translationKey: 'settings.apps.propertiesToSync.LastConsentDate',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyRevision,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyRevision',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyVersion,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyVersion',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
          ],
          optionsCheckAll: true,
        },
      ],
      [
        {
          type: IntegrationInputType.TextArea,
          key: 'properties',
          required: false,
          labelKey: 'settings.apps.Custobar.properties-label',
          descriptionKey: 'settings.apps.Custobar.properties-descr',
          propertyConversion: IntegrationPropertyConversion.NewLineToCsv,
          uniqueLineKey: IntegrationLineKeyPart.AfterLastEquals,
          placeholderKey: 'settings.apps.Custobar.properties-placeholder',
          defaultValue:
            'Phone number=phone_number\n' +
            'First name=first_name\n' +
            'Last name=last_name\n' +
            'Email Consent=can_email\n' +
            'SMS Consent=can_sms\n' +
            'Postal Consent=can_post\n' +
            'Push Consent=can_push\n' +
            'Birthday=birth_date\n' +
            'Country=country\n' +
            'Gender=gender\n' +
            'Language=language',
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static APSIS_CONFIG = {
    type: IntegrationType.Apsis,
    logoUrl: '/assets/img/logos/apsis.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11402901-integrate-with-apsis-pro',
    descriptionRows: [
      { textKey: 'settings.apps.Apsis.description1' } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Apsis.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Apsis.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Apsis.description4',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Apsis.description5',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Apsis.description6',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiKey',
        required: true,
        labelKey: 'settings.apps.fields.apiKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiKey',
          descriptionKey: 'settings.apps.Apsis.apiKeyExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'listIdsForTrigger',
          required: false,
          labelKey: 'settings.apps.Apsis.lists-access-label',
          // descriptionKey: 'settings.apps.Apsis.lists-descr',
          optionsUrl: '/integrations/Apsis/lists',
        },
      ],
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          labelKey: 'settings.apps.Apsis.properties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentCheckboxPresent,
              translationKey: 'settings.apps.propertiesToSync.LastConsentCheckboxPresent',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextDomain,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextDomain',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentContextTitle,
              translationKey: 'settings.apps.propertiesToSync.LastConsentContextTitle',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentDate,
              translationKey: 'settings.apps.propertiesToSync.LastConsentDate',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyRevision,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyRevision',
            },
            {
              key: IntegrationDropdownOptionValues.LastConsentPolicyVersion,
              translationKey: 'settings.apps.propertiesToSync.LastConsentPolicyVersion',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
            // These two are removed according to Olofs recommendation
            /*{key: IntegrationDropdownOptionValues.Name, translationKey: 'settings.apps.propertiesToSync.Name'},
            {key: IntegrationDropdownOptionValues.Optin, translationKey: 'settings.apps.propertiesToSync.Optin'},
            {key: IntegrationDropdownOptionValues.OptinData, translationKey: 'settings.apps.propertiesToSync.OptinData'},
            {key: IntegrationDropdownOptionValues.Org, translationKey: 'settings.apps.propertiesToSync.Org'},
            {key: IntegrationDropdownOptionValues.Rating, translationKey: 'settings.apps.propertiesToSync.Rating'},
            {key: IntegrationDropdownOptionValues.RatingM, translationKey: 'settings.apps.propertiesToSync.RatingM'}*/
          ],
          optionsCheckAll: true,
        },
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'listIdsToSync',
          required: false,
          labelKey: 'settings.apps.Apsis.lists-label',
          optionsUrl: '/integrations/Apsis/lists',
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static APSIS_ONE_CONFIG = {
    type: IntegrationType.ApsisOne,
    logoUrl: '/assets/img/logos/apsis-one.jpg',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11402885-integrate-with-apsis-one',
    descriptionRows: [
      {
        textKey: 'settings.apps.ApsisOne.description1',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.ApsisOne.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.ApsisOne.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.ApsisOne.description4',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.ApsisOne.description5',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.ApsisOne.description6',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'clientId',
        required: true,
        labelKey: 'settings.apps.fields.apiKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiKey',
          descriptionKey: 'settings.apps.ApsisOne.clientIdExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'clientSecret',
        required: true,
        labelKey: 'settings.apps.ApsisOne.clientSecret',
        explainerContent: {
          headerKey: 'settings.apps.ApsisOne.clientSecret',
          descriptionKey: 'settings.apps.ApsisOne.clientSecretExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.SingleChoiceDropdown,
          key: 'sectionDiscriminator',
          required: false,
          labelKey: 'settings.apps.ApsisOne.section-label',
          optionsUrl: '/integrations/settings/apsis-one/sections',
          optionsRefreshAfterChoice: ['emailFieldId'],
          optionsIdPropertyName: 'discriminator',
          optionsCustomParsing: IntegrationOptionsParsingType.ApsisOneSections,
        },
        {
          type: IntegrationInputType.SingleChoiceDropdown,
          key: 'keyspaceDiscriminator',
          required: false,
          labelKey: 'settings.apps.ApsisOne.keyspace-label',
          optionsUrl: '/integrations/settings/apsis-one/keyspaces',
          optionsIdPropertyName: 'discriminator',
          optionsCustomParsing: IntegrationOptionsParsingType.ApsisOneKeyspaces,
        },
        {
          type: IntegrationInputType.SingleChoiceDropdown,
          key: 'emailFieldId',
          required: false,
          labelKey: 'settings.apps.ApsisOne.emailField-label',
          optionsUrl: '/integrations/settings/apsis-one/{sectionDiscriminator}/attributes',
          optionsIdPropertyName: 'id',
          optionsUseValuesFromChoice: ['sectionDiscriminator'],
          optionsCustomParsing: IntegrationOptionsParsingType.ApsisOneEmailFieldIds,
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static SIFT_LAB_CONFIG = {
    type: IntegrationType.SiftLab,
    logoUrl: '/assets/img/logos/sift-logo.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11406562-integrate-with-sift-lab',
    paidAddon: true,
    descriptionRows: [
      {
        textKey: 'settings.apps.SiftLab.description1',
      } as IntegrationDetailsRow,
      { paidAddon: 'settings.apps.SiftLab.paidAddon' } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiKey',
        required: true,
        labelKey: 'settings.apps.fields.apiKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiKey',
          descriptionKey: 'settings.apps.SiftLab.apiKeyExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'url',
        required: true,
        labelKey: 'settings.apps.fields.url',
        explainerContent: {
          headerKey: 'settings.apps.fields.url',
          descriptionKey: 'settings.apps.SiftLab.urlExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.SingleChoiceDropdown,
          key: 'dataModel',
          required: false,
          labelKey: 'settings.apps.SiftLab.dataModel-label',
          optionsUrl: '/integrations/settings/sift-lab/data-models',
        },
        {
          type: IntegrationInputType.InputField,
          subtype: IntegrationInputSubType.Text,
          key: 'idColumn',
          required: false,
          labelKey: 'settings.apps.SiftLab.idColumn-label',
          placeholderKey: 'settings.apps.SiftLab.idColumn-placeholder',
          instruction: 'settings.apps.SiftLab.idColumn-instruction',
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static ZAPIER_CONFIG = {
    type: IntegrationType.Zapier,
    logoUrl: '/assets/img/logos/zapier.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11059188-integrate-with-zapier',
    descriptionRows: [{ textKey: 'settings.apps.Zapier.description1' } as IntegrationDetailsRow],
    authenticationForm: [] as Array<IntegrationInputConfig>,
    configurationForm: [] as Array<Array<IntegrationInputConfig>>,
    restApiInformation: {} as RestApiInformation,
  } as IntegrationConfig;

  private static PIPEDRIVE_CONFIG = {
    type: IntegrationType.Pipedrive,
    logoUrl: 'assets/img/logos/pipedrive-logo-bullet.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11065372-integrate-with-pipedrive',
    descriptionRows: [
      {
        textKey: 'settings.apps.Pipedrive.description1',
      } as IntegrationDetailsRow,
      {
        textKey: 'settings.apps.Pipedrive.description2',
      } as IntegrationDetailsRow,
      {
        textKey: 'settings.apps.Pipedrive.description3',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiToken',
        required: true,
        labelKey: 'settings.apps.fields.apiKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiKey',
          descriptionKey: 'settings.apps.Pipedrive.apiKeyExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
  };

  public static SHOPIFY_CONFIG = {
    type: IntegrationType.Shopify,
    logoUrl: '/assets/img/logos/shopify_glyph.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/11047096-install-with-shopify',
    adminOnly: false,
    isBeta: false,
    descriptionRows: [
      { textKey: 'settings.apps.Shopify.description1' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Shopify.description2' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Shopify.description3' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Shopify.description4' } as IntegrationDetailsRow,
    ],
    authenticationForm: [] as Array<IntegrationInputConfig>,
    configurationForm: [] as Array<Array<IntegrationInputConfig>>,
    restApiInformation: {
      textKey: 'settings.apps.Shopify.installAppMessage',
    } as RestApiInformation,
  } as IntegrationConfig;

  public static BREVO_CONFIG = {
    type: IntegrationType.Brevo,
    logoUrl: '/assets/img/logos/brevo.jpg',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/12710056-integrating-with-brevo',
    descriptionRows: [
      {
        textKey: 'settings.apps.Brevo.description1',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Brevo.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Brevo.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Brevo.description4',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Brevo.description5',
      } as IntegrationDetailsRow,
    ],

    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiKey',
        required: true,
        labelKey: 'settings.apps.fields.apiKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiKey',
          descriptionKey: 'settings.apps.Brevo.apiKeyExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          instruction: 'settings.apps.Brevo.properties-instruction',
          labelKey: 'settings.apps.Brevo.profileProperties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
          ],
          optionsCheckAll: true,
        },
        {
          type: IntegrationInputType.ToggleButton,
          key: 'createMissingContactAttributes',
          labelKey: 'settings.apps.Brevo.create-properties-explanation-header',
          instruction: 'settings.apps.Brevo.properties-instruction',
          placeholderKey: 'settings.apps.Brevo.create-properties-label',
          explainerContent: {
            headerKey: 'settings.apps.Brevo.create-properties-explanation-header',
            descriptionKey: 'settings.apps.Brevo.create-properties-explanation-description',
          },
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  public static DOTDIGITAL_CONFIG = {
    type: IntegrationType.Dotdigital,
    logoUrl: '/assets/img/logos/dotdigital.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/13038843-integrate-with-dotdigital',
    descriptionRows: [
      {
        textKey: 'settings.apps.Dotdigital.description1',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Dotdigital.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Dotdigital.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Dotdigital.description4',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Dotdigital.description5',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Dotdigital.description6',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'apiUser',
        required: true,
        labelKey: 'settings.apps.Dotdigital.apiUser',
        explainerContent: {
          headerKey: 'settings.apps.Dotdigital.apiUser',
          descriptionKey: 'settings.apps.Dotdigital.apiUserExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiPassword',
        required: true,
        labelKey: 'settings.apps.Dotdigital.apiPassword',
        explainerContent: {
          headerKey: 'settings.apps.Dotdigital.apiPassword',
          descriptionKey: 'settings.apps.Dotdigital.apiPasswordExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          instruction: 'settings.apps.Dotdigital.properties-instruction',
          labelKey: 'settings.apps.Dotdigital.profileProperties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
          ],
          optionsCheckAll: true,
        },
        {
          type: IntegrationInputType.ToggleButton,
          key: 'createMissingContactAttributes',
          labelKey: 'settings.apps.Dotdigital.create-properties-explanation-header',
          instruction: 'settings.apps.Dotdigital.properties-instruction',
          placeholderKey: 'settings.apps.Dotdigital.create-properties-label',
          explainerContent: {
            headerKey: 'settings.apps.Dotdigital.create-properties-explanation-header',
            descriptionKey: 'settings.apps.Dotdigital.create-properties-explanation-description',
          },
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  public static OMNISEND_CONFIG = {
    type: IntegrationType.Omnisend,
    logoUrl: '/assets/img/logos/omnisend.png',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/14299929-integrate-with-omnisend',
    descriptionRows: [
      {
        textKey: 'settings.apps.Omnisend.description1',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Omnisend.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Omnisend.description3',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Omnisend.description4',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiKey',
        required: true,
        labelKey: 'settings.apps.fields.apiKey',
        explainerContent: {
          headerKey: 'settings.apps.fields.apiKey',
          descriptionKey: 'settings.apps.Omnisend.apiKeyExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.MultiChoiceDropdown,
          key: 'propertiesToSync',
          required: false,
          instruction: 'settings.apps.Omnisend.properties-instruction',
          labelKey: 'settings.apps.Omnisend.profileProperties-label',
          options: [
            {
              key: IntegrationDropdownOptionValues.FirstSeen,
              translationKey: 'settings.apps.propertiesToSync.FirstSeen',
            },
            {
              key: IntegrationDropdownOptionValues.Goals,
              translationKey: 'settings.apps.propertiesToSync.Goals',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN1,
              translationKey: 'settings.apps.propertiesToSync.ProflN1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV1,
              translationKey: 'settings.apps.propertiesToSync.ProflV1',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN2,
              translationKey: 'settings.apps.propertiesToSync.ProflN2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV2,
              translationKey: 'settings.apps.propertiesToSync.ProflV2',
            },
            {
              key: IntegrationDropdownOptionValues.ProflN3,
              translationKey: 'settings.apps.propertiesToSync.ProflN3',
            },
            {
              key: IntegrationDropdownOptionValues.ProflV3,
              translationKey: 'settings.apps.propertiesToSync.ProflV3',
            },
            {
              key: IntegrationDropdownOptionValues.LastSeen,
              translationKey: 'settings.apps.propertiesToSync.LastSeen',
            },
          ],
          optionsCheckAll: true,
        },
        {
          type: IntegrationInputType.ToggleButton,
          key: 'addTriggerbeeSourceTag',
          defaultValue: 'true',
          labelKey: 'settings.apps.Omnisend.automatic-source-tag-explanation-header',
          placeholderKey: 'settings.apps.Omnisend.automatic-source-tag-label',
          explainerContent: {
            headerKey: 'settings.apps.Omnisend.automatic-source-tag-explanation-header',
            descriptionKey: 'settings.apps.Omnisend.automatic-source-tag-explanation-description',
          },
        },
      ],
      [
        {
          type: IntegrationInputType.SegmentPicker,
          key: 'segments',
          required: false,
          labelKey: 'settings.apps.Omnisend.segments-label',
          descriptionKey: 'settings.apps.Omnisend.segments-picker-descr',
          propertyConversion: IntegrationPropertyConversion.NewLineToCsv,
          urlStripRegex: '\\/editor\\/([a-f0-9]+)',
        },
        {
          type: IntegrationInputType.TextArea,
          key: 'tags',
          required: false,
          labelKey: 'settings.apps.Omnisend.tags-label',
          descriptionKey: 'settings.apps.Omnisend.tags-descr',
          propertyConversion: IntegrationPropertyConversion.NewLineToCsv,
          placeholderKey: 'settings.apps.Omnisend.tags-placeholder',
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  private static CLEVERREACH_CONFIG = {
    type: IntegrationType.CleverReach,
    logoUrl: '/assets/img/logos/cleverreach.svg',
    useOAuth: true,
    instructionsUrl: 'https://help.triggerbee.com/en/articles/14495059-integrate-with-cleverreach',
    descriptionRows: [
      { textKey: 'settings.apps.CleverReach.description1' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.CleverReach.description2' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.CleverReach.description3' } as IntegrationDetailsRow,
    ],
    authenticationForm: [] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.SingleChoiceDropdown,
          key: 'group',
          required: true,
          labelKey: 'settings.apps.CleverReach.list-label',
          optionsUrl: '/integrations/CleverReach/lists',
        },
        {
          type: IntegrationInputType.ToggleButton,
          key: 'enforceDoubleOptIn',
          defaultValue: true,
          labelKey: 'settings.apps.CleverReach.double-opt-in-explanation-header',
          placeholderKey: 'settings.apps.CleverReach.double-opt-in-label',
          explainerContent: {
            headerKey: 'settings.apps.CleverReach.double-opt-in-explanation-header',
            descriptionKey: 'settings.apps.CleverReach.double-opt-in-explanation-description',
          },
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  public static SHOPWARE_CONFIG = {
    type: IntegrationType.Shopware,
    logoUrl: '/assets/img/logos/shopware.svg',
    instructionsUrl: 'https://help.triggerbee.com/en/articles/14495055-integrate-with-shopware',
    descriptionRows: [
      {
        textKey: 'settings.apps.Shopware.description1',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Shopware.description2',
      } as IntegrationDetailsRow,
      {
        bulletPoint: 'settings.apps.Shopware.description3',
      } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'shopUrl',
        required: true,
        labelKey: 'settings.apps.Shopware.shopUrl',
        placeholderKey: 'settings.apps.Shopware.shopUrlPlaceholder',
        explainerContent: {
          headerKey: 'settings.apps.Shopware.shopUrl',
          descriptionKey: 'settings.apps.Shopware.shopUrlExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'accessKeyId',
        required: true,
        labelKey: 'settings.apps.Shopware.accessKeyId',
        explainerContent: {
          headerKey: 'settings.apps.Shopware.accessKeyId',
          descriptionKey: 'settings.apps.Shopware.accessKeyIdExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'secretAccessKey',
        required: true,
        labelKey: 'settings.apps.Shopware.secretAccessKey',
        explainerContent: {
          headerKey: 'settings.apps.Shopware.secretAccessKey',
          descriptionKey: 'settings.apps.Shopware.secretAccessKeyExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.SingleChoiceDropdown,
          key: 'defaultSalutationId',
          required: true,
          labelKey: 'settings.apps.Shopware.defaultSalutationId',
          optionsUrl: '/integrations/Shopware/salutations',
        },
      ],
      [
        {
          type: IntegrationInputType.SingleChoiceDropdown,
          key: 'defaultPaymentMethodId',
          required: false,
          labelKey: 'settings.apps.Shopware.defaultPaymentMethodId',
          optionsUrl: '/integrations/Shopware/payment-methods',
          explainerContent: {
            headerKey: 'settings.apps.Shopware.defaultPaymentMethodId',
            descriptionKey: 'settings.apps.Shopware.defaultPaymentMethodIdExplainer',
          },
        },
      ],
      [
        {
          type: IntegrationInputType.SingleChoiceDropdown,
          key: 'defaultCountryId',
          required: false,
          labelKey: 'settings.apps.Shopware.defaultCountryId',
          optionsUrl: '/integrations/Shopware/countries',
          explainerContent: {
            headerKey: 'settings.apps.Shopware.defaultCountryId',
            descriptionKey: 'settings.apps.Shopware.defaultCountryIdExplainer',
          },
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;

  public static CUSTOM_CONFIG = {
    type: IntegrationType.Custom,
    logoUrl: '/assets/img/http.svg',
    isNew: true,
    useOAuth: true,
    oauthRequiresSettings: true,
    instructionsUrl: 'https://help.triggerbee.com/en/articles/15250619-custom-integration',
    descriptionRows: [
      { textKey: 'settings.apps.Custom.description1' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Custom.description2' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Custom.description3' } as IntegrationDetailsRow,
      { bulletPoint: 'settings.apps.Custom.description4' } as IntegrationDetailsRow,
    ],
    authenticationForm: [
      {
        type: IntegrationInputType.SingleChoiceDropdown,
        key: 'authenticationType',
        required: true,
        defaultValue: CustomAuthenticationType.OAuth,
        labelKey: 'settings.apps.Custom.authenticationType',
        options: [
          { key: CustomAuthenticationType.OAuth, translationKey: 'settings.apps.Custom.authenticationTypeOAuth' },
          { key: CustomAuthenticationType.ApiKey, translationKey: 'settings.apps.Custom.authenticationTypeApiKey' },
          { key: CustomAuthenticationType.None, translationKey: 'generic.none' },
        ],
        explainerContent: {
          headerKey: 'settings.apps.Custom.authenticationType',
          descriptionKey: 'settings.apps.Custom.authenticationTypeExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'clientId',
        required: true,
        labelKey: 'settings.apps.Custom.clientId',
        visibleWhen: { key: 'authenticationType', values: [CustomAuthenticationType.OAuth] },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'clientSecret',
        required: true,
        labelKey: 'settings.apps.Custom.clientSecret',
        visibleWhen: { key: 'authenticationType', values: [CustomAuthenticationType.OAuth] },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'authorizeUrl',
        required: true,
        labelKey: 'settings.apps.Custom.authorizeUrl',
        placeholderKey: 'settings.apps.Custom.authorizeUrlPlaceholder',
        visibleWhen: { key: 'authenticationType', values: [CustomAuthenticationType.OAuth] },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'tokenUrl',
        required: true,
        labelKey: 'settings.apps.Custom.tokenUrl',
        placeholderKey: 'settings.apps.Custom.tokenUrlPlaceholder',
        visibleWhen: { key: 'authenticationType', values: [CustomAuthenticationType.OAuth] },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'scope',
        required: false,
        labelKey: 'settings.apps.Custom.scope',
        visibleWhen: { key: 'authenticationType', values: [CustomAuthenticationType.OAuth] },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Password,
        key: 'apiKey',
        required: true,
        labelKey: 'settings.apps.Custom.apiKey',
        placeholderKey: 'settings.apps.Custom.apiKeyPlaceholder',
        visibleWhen: { key: 'authenticationType', values: [CustomAuthenticationType.ApiKey] },
        explainerContent: {
          headerKey: 'settings.apps.Custom.apiKey',
          descriptionKey: 'settings.apps.Custom.apiKeyExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'apiKeyHeaderName',
        required: false,
        labelKey: 'settings.apps.Custom.apiKeyHeaderName',
        placeholderKey: 'settings.apps.Custom.apiKeyHeaderNamePlaceholder',
        visibleWhen: { key: 'authenticationType', values: [CustomAuthenticationType.ApiKey] },
        explainerContent: {
          headerKey: 'settings.apps.Custom.apiKeyHeaderName',
          descriptionKey: 'settings.apps.Custom.apiKeyHeaderNameExplainer',
        },
      },
      {
        type: IntegrationInputType.InputField,
        subtype: IntegrationInputSubType.Text,
        key: 'authenticationTestUrl',
        required: false,
        labelKey: 'settings.apps.Custom.authenticationTestUrl',
        placeholderKey: 'settings.apps.Custom.authenticationTestUrlPlaceholder',
        visibleWhen: { key: 'authenticationType', values: [CustomAuthenticationType.ApiKey] },
        explainerContent: {
          headerKey: 'settings.apps.Custom.authenticationTestUrl',
          descriptionKey: 'settings.apps.Custom.authenticationTestUrlExplainer',
        },
      },
    ] as Array<IntegrationInputConfig>,
    configurationForm: [
      [
        {
          type: IntegrationInputType.InputField,
          subtype: IntegrationInputSubType.Text,
          key: 'name',
          required: false,
          labelKey: 'settings.apps.Custom.displayName',
          placeholderKey: 'settings.apps.Custom.displayNamePlaceholder',
          explainerContent: {
            headerKey: 'settings.apps.Custom.displayName',
            descriptionKey: 'settings.apps.Custom.displayNameExplainer',
          },
        },
        {
          type: IntegrationInputType.InputField,
          subtype: IntegrationInputSubType.Text,
          key: 'iconUrl',
          required: false,
          isUrl: true,
          labelKey: 'settings.apps.Custom.iconUrl',
          placeholderKey: 'settings.apps.Custom.iconUrlPlaceholder',
          explainerContent: {
            headerKey: 'settings.apps.Custom.iconUrl',
            descriptionKey: 'settings.apps.Custom.iconUrlExplainer',
          },
        },
      ],
      [
        {
          type: IntegrationInputType.InputField,
          subtype: IntegrationInputSubType.Text,
          key: 'segmentsListingUrl',
          required: false,
          isUrl: true,
          labelKey: 'settings.apps.Custom.segmentsListingUrl',
          placeholderKey: 'settings.apps.Custom.segmentsListingUrlPlaceholder',
          explainerContent: {
            headerKey: 'settings.apps.Custom.segmentsListingUrl',
            descriptionKey: 'settings.apps.Custom.segmentsListingUrlExplainer',
          },
        },
        {
          type: IntegrationInputType.InputField,
          subtype: IntegrationInputSubType.Text,
          key: 'segmentsListingIdPath',
          required: false,
          labelKey: 'settings.apps.Custom.segmentsListingIdPath',
          placeholderKey: 'settings.apps.Custom.segmentsListingIdPathPlaceholder',
          explainerContent: {
            headerKey: 'settings.apps.Custom.segmentsListingIdPath',
            descriptionKey: 'settings.apps.Custom.segmentsListingIdPathExplainer',
          },
        },
        {
          type: IntegrationInputType.InputField,
          subtype: IntegrationInputSubType.Text,
          key: 'segmentsListingNamePath',
          required: false,
          labelKey: 'settings.apps.Custom.segmentsListingNamePath',
          placeholderKey: 'settings.apps.Custom.segmentsListingNamePathPlaceholder',
          explainerContent: {
            headerKey: 'settings.apps.Custom.segmentsListingNamePath',
            descriptionKey: 'settings.apps.Custom.segmentsListingNamePathExplainer',
          },
        },
        {
          type: IntegrationInputType.InputField,
          subtype: IntegrationInputSubType.Text,
          key: 'segmentsCheckingUrl',
          required: false,
          isUrl: true,
          labelKey: 'settings.apps.Custom.segmentsCheckingUrl',
          placeholderKey: 'settings.apps.Custom.segmentsCheckingUrlPlaceholder',
          explainerContent: {
            headerKey: 'settings.apps.Custom.segmentsCheckingUrl',
            descriptionKey: 'settings.apps.Custom.segmentsCheckingUrlExplainer',
          },
        },
        {
          type: IntegrationInputType.InputField,
          subtype: IntegrationInputSubType.Text,
          key: 'segmentsSubscriberCheckResponsePath',
          required: false,
          labelKey: 'settings.apps.Custom.segmentsSubscriberCheckResponsePath',
          placeholderKey: 'settings.apps.Custom.segmentsSubscriberCheckResponsePathPlaceholder',
          explainerContent: {
            headerKey: 'settings.apps.Custom.segmentsSubscriberCheckResponsePath',
            descriptionKey: 'settings.apps.Custom.segmentsSubscriberCheckResponsePathExplainer',
          },
        },
      ],
    ] as Array<Array<IntegrationInputConfig>>,
  } as IntegrationConfig;
}
